import { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader2, RefreshCw, AlertTriangle, Clock } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../api/client';
import KpiSections from '../components/KpiSections';
import { buildKpiValues } from '../config/buildKpiValues';

const RANGE_DAYS = { '7': 7, '14': 14, '30': 30 };

// null from the API means "nothing to measure" (no completed sessions, nobody
// played) — shown as a dash, never as a 0 that would read as a quiet day.
const dash = (v, suffix = '') => (v == null ? '—' : `${v}${suffix}`);

function shortDate(iso) {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

// MIS — the management information page. Every KPI built so far lives here, laid
// out Daily / Weekly / Monthly (KpiSections), followed by the full player and
// session detail: the sessions-per-day chart, the player figures for today / 7
// days / 30 days, and the time played. Every number comes from an existing
// /internal endpoint; this page only lays them out and says what each one means.
// The Overview dashboard deliberately shows none of it.
export default function MIS() {
  const [days, setDays] = useState('7');
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pure fetch — returns what it found and touches no state, so an effect can
  // call it and set state only once the data has arrived.
  const fetchAll = useCallback(async () => {
    // Each call is optional so one failing endpoint blanks only its own
    // section, not the whole page.
    const [
      daily, players, mix, repeat, hours, duration, dashboard, recharge, fnb, nolag, cashOnline, pcFnb, byHour,
      perScreen, perSession, tickets, payments, uptime, weekly, usage, reinstalls, activation, byCafe, mom, topGames, adoption, dashUsage,
      supportByCafe,
    ] = await Promise.all([
      api.get('/internal/sessions/daily', { params: { days: RANGE_DAYS[days] } }).catch(() => null),
      api.get('/internal/players/activity').catch(() => null),
      api.get('/internal/players/new-vs-returning').catch(() => null),
      api.get('/internal/players/repeat').catch(() => null),
      api.get('/internal/sessions/hours').catch(() => null),
      api.get('/internal/sessions/duration').catch(() => null),
      api.get('/internal/dashboard').catch(() => null),
      api.get('/internal/revenue/recharge').catch(() => null),
      api.get('/internal/revenue/fnb').catch(() => null),
      api.get('/internal/revenue/nolag').catch(() => null),
      api.get('/internal/revenue/cash-vs-online').catch(() => null),
      api.get('/internal/revenue/pc-vs-fnb').catch(() => null),
      api.get('/internal/revenue/by-hour').catch(() => null),
      api.get('/internal/revenue/per-screen').catch(() => null),
      api.get('/internal/revenue/per-session').catch(() => null),
      api.get('/internal/support/health').catch(() => null),
      api.get('/internal/payments/issues').catch(() => null),
      api.get('/internal/health/uptime').catch(() => null),
      api.get('/internal/performance/weekly').catch(() => null),
      api.get('/internal/product/usage').catch(() => null),
      api.get('/internal/fleet/reinstalls').catch(() => null),
      api.get('/internal/fleet/activation').catch(() => null),
      api.get('/internal/revenue/by-cafe').catch(() => null),
      api.get('/internal/revenue/month-on-month').catch(() => null),
      api.get('/internal/activity/top-games').catch(() => null),
      api.get('/internal/activity/feature-adoption').catch(() => null),
      api.get('/internal/activity/dashboard-usage').catch(() => null),
      api.get('/internal/support/by-cafe').catch(() => null),
    ]);
    const next = {
      daily: daily?.data, players: players?.data, mix: mix?.data,
      repeat: repeat?.data, hours: hours?.data, duration: duration?.data,
      dashboard: dashboard?.data, recharge: recharge?.data, fnb: fnb?.data, nolag: nolag?.data,
      cashOnline: cashOnline?.data, pcFnb: pcFnb?.data, byHour: byHour?.data, perScreen: perScreen?.data,
      perSession: perSession?.data, tickets: tickets?.data, payments: payments?.data,
      uptime: uptime?.data, weekly: weekly?.data, usage: usage?.data, reinstalls: reinstalls?.data,
      activation: activation?.data, byCafe: byCafe?.data, mom: mom?.data,
      topGames: topGames?.data, adoption: adoption?.data, dashUsage: dashUsage?.data,
      supportByCafe: supportByCafe?.data,
    };
    const failed = Object.values(next).every((v) => !v);
    return { next, error: failed ? 'Could not load the MIS figures.' : null };
  }, [days]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await fetchAll();
      if (cancelled) return;
      setData(result.next);
      setError(result.error);
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchAll]);

  const load = async () => {
    setIsLoading(true);
    const result = await fetchAll();
    setData(result.next);
    setError(result.error);
    setIsLoading(false);
  };

  const chart = (data?.daily?.days || []).map((d) => ({ name: shortDate(d.date), sessions: d.sessions }));
  const { players, mix, repeat, hours, duration, daily } = data || {};

  // 24 bars, one per café-local hour; "12a, 1a ... 12p, 1p ..." keeps the axis readable.
  // null (not measured) stays null so the card says the data is not available instead
  // of drawing 24 flat zeros that would read as a dead week.
  const hourBars = data?.byHour?.this_week?.hours
    ? data.byHour.this_week.hours.map((amount, h) => ({
      label: `${h % 12 || 12}${h < 12 ? 'a' : 'p'}`,
      amount,
    }))
    : null;

  // The tiles' numbers. Without the dashboard summary the café and screen tiles
  // can't be built, so the values stay empty and those tiles read "Could not load"
  // instead of showing zeros.
  const kpiValues = useMemo(
    () => (data ? buildKpiValues({
      dash: data.dashboard, players: data.players, sessions: data.daily, duration: data.duration,
      hours: data.hours, repeat: data.repeat, mix: data.mix, recharge: data.recharge, fnb: data.fnb,
      nolag: data.nolag, cashOnline: data.cashOnline, pcFnb: data.pcFnb, byHour: data.byHour,
      perScreen: data.perScreen, perSession: data.perSession, tickets: data.tickets, payments: data.payments,
      uptime: data.uptime, weekly: data.weekly, usage: data.usage, reinstalls: data.reinstalls,
      activation: data.activation, byCafe: data.byCafe, mom: data.mom,
      topGames: data.topGames, adoption: data.adoption, dashUsage: data.dashUsage,
      supportByCafe: data.supportByCafe,
    }) : {}),
    [data]
  );

  return (
    <div className="mis-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">MIS</h1>
          <p className="page-subtitle">Every KPI across your network, by day, week and month</p>
        </div>
        <div className="header-actions">
          <select value={days} onChange={(e) => setDays(e.target.value)} className="input select time-select" aria-label="Sessions chart range">
            <option value="7">Last 7 days</option>
            <option value="14">Last 14 days</option>
            <option value="30">Last 30 days</option>
          </select>
          <button className="btn btn--secondary" onClick={load} disabled={isLoading}>
            <RefreshCw size={16} className={isLoading ? 'spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="pa-error"><AlertTriangle size={16} /> {error}</div>
      )}

      {isLoading && !data ? (
        <div className="pa-loading"><Loader2 size={28} className="spin" /></div>
      ) : !data ? null : (
        <>
          <KpiSections values={kpiValues} />

          <h3 className="mis-detail-title">Player and session detail</h3>

          <div className="pa-metrics">
            <div className="pa-card">
              <span className="pa-metric__label">Sessions today</span>
              <span className="pa-metric__value">{dash(daily?.sessions_today)}</span>
            </div>
            <div className="pa-card">
              <span className="pa-metric__label">Avg sessions / day</span>
              <span className="pa-metric__value">{dash(daily?.average_per_day)}</span>
              <span className="pa-metric__sub">previous {days} complete days</span>
            </div>
            <div className="pa-card">
              <span className="pa-metric__label">Gaming hours today</span>
              <span className="pa-metric__value">{dash(hours?.hours_today)}</span>
            </div>
            <div className="pa-card">
              <span className="pa-metric__label">Avg session length today</span>
              <span className="pa-metric__value">{dash(duration?.avg_today_minutes, ' min')}</span>
            </div>
          </div>

          <div className="pa-card pa-chart">
            <h3 className="pa-title">Sessions per day</h3>
            {chart.length === 0 ? (
              <div className="pa-empty"><Clock size={20} /><span>No session data available.</span></div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={chart}>
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 12 }} />
                  <YAxis allowDecimals={false} axisLine={false} tickLine={false} width={32} tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 12 }} />
                  <Tooltip
                    cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                    contentStyle={{ background: 'rgba(26,26,36,0.95)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', color: 'white' }}
                  />
                  <Bar dataKey="sessions" fill="#FE7B00" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
            <p className="pa-note">Sessions are counted on the day they started (café-local time). The last bar is today so far.</p>
          </div>

          <div className="pa-card pa-chart">
            <h3 className="pa-title">Money in by hour of the day (this week)</h3>
            {!hourBars ? (
              <div className="pa-empty"><Clock size={20} /><span>Hourly money-in data is not available.</span></div>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={hourBars}>
                  <XAxis dataKey="label" interval={1} axisLine={false} tickLine={false} tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} />
                  <YAxis axisLine={false} tickLine={false} width={48} tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 12 }} />
                  <Tooltip
                    cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                    formatter={(v) => [`₹${Number(v).toLocaleString('en-IN')}`, 'Money in']}
                    contentStyle={{ background: 'rgba(26,26,36,0.95)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', color: 'white' }}
                  />
                  <Bar dataKey="amount" fill="#FE7B00" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
            <p className="pa-note">
              Money is counted when it arrived: a top-up shows in the hour it was put in, not when it is played. Hours are
              café-local (India time) and add up over the whole week.
            </p>
          </div>

          <div className="pa-card">
            <h3 className="pa-title">Players</h3>
            <div className="pa-table-wrap">
              <table className="pa-table">
                <thead>
                  <tr><th /><th>Today</th><th>Last 7 days</th><th>Last 30 days</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Unique players</td>
                    <td>{dash(players?.active_today)}</td>
                    <td>{dash(players?.active_7d)}</td>
                    <td>{dash(players?.active_30d)}</td>
                  </tr>
                  <tr>
                    <td>New players — first-ever session in the period</td>
                    <td>{dash(mix?.new_today)}</td>
                    <td>{dash(mix?.new_7d)}</td>
                    <td>{dash(mix?.new_30d)}</td>
                  </tr>
                  <tr>
                    <td>Returning players — had played before</td>
                    <td>{dash(mix?.returning_today)}</td>
                    <td>{dash(mix?.returning_7d)}</td>
                    <td>{dash(mix?.returning_30d)}</td>
                  </tr>
                  <tr>
                    <td>Newly signed up (account created)</td>
                    <td>{dash(players?.new_today)}</td>
                    <td>{dash(players?.new_7d)}</td>
                    <td>{dash(players?.new_30d)}</td>
                  </tr>
                  <tr>
                    <td>Repeat players — played on 2+ different days</td>
                    <td>—</td>
                    <td>{repeat ? `${repeat.repeat_7d} of ${repeat.players_7d} (${dash(repeat.repeat_rate_7d, '%')})` : '—'}</td>
                    <td>{repeat ? `${repeat.repeat_30d} of ${repeat.players_30d} (${dash(repeat.repeat_rate_30d, '%')})` : '—'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="pa-card">
            <h3 className="pa-title">Time played</h3>
            <div className="pa-table-wrap">
              <table className="pa-table">
                <thead>
                  <tr><th /><th>Today</th><th>Last 7 days</th><th>Last 30 days</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Total gaming hours</td>
                    <td>{dash(hours?.hours_today, ' h')}</td>
                    <td>{dash(hours?.hours_7d, ' h')}</td>
                    <td>{dash(hours?.hours_30d, ' h')}</td>
                  </tr>
                  <tr>
                    <td>Average session length</td>
                    <td>{dash(duration?.avg_today_minutes, ' min')}{duration ? ` (${duration.sessions_today})` : ''}</td>
                    <td>{dash(duration?.avg_7d_minutes, ' min')}{duration ? ` (${duration.sessions_7d})` : ''}</td>
                    <td>{dash(duration?.avg_30d_minutes, ' min')}{duration ? ` (${duration.sessions_30d})` : ''}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="pa-note">Numbers in brackets are the completed sessions behind each average.</p>
          </div>

          <div className="pa-card">
            <h3 className="pa-title">What these numbers mean</h3>
            <ul className="pa-defs">
              <li><strong>Unique players</strong> — people with a session in the period, each counted once even if they play in several cafés.</li>
              <li><strong>New vs returning</strong> — of the people who played in the period, new ones had never played before it; returning ones had.</li>
              <li><strong>Newly signed up</strong> — accounts created in the period (staff and deleted customers excluded). Not the same as new players: someone can sign up and not play yet.</li>
              <li><strong>Repeat players</strong> — played on two or more different days in the period. Several sessions in one evening is still one day.</li>
              <li><strong>Gaming hours</strong> — time customer sessions were open on PCs, counted only for the part inside the period. Same measure as the café Statistics &ldquo;PC usage&rdquo; card.</li>
              <li><strong>Average session length</strong> — only completed sessions; one that ends before it starts counts as zero. Sessions closed as &ldquo;abandoned&rdquo; use an estimated end time, so they can read long.</li>
              <li><strong>Today</strong> is the café-local day (India time). Cafés that can&apos;t be reached count as zero, so totals can read low.</li>
            </ul>
          </div>
        </>
      )}

      <style>{`
        .mis-page { max-width: 1200px; }
        .mis-page .page-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: var(--space-8); gap: var(--space-4); flex-wrap: wrap; }
        .mis-page .page-title { font-size: var(--text-2xl); font-weight: 600; margin-bottom: var(--space-2); letter-spacing: -0.02em; }
        .mis-page .page-subtitle { font-size: var(--text-sm); color: var(--text-tertiary); }
        .mis-page .header-actions { display: flex; gap: var(--space-3); align-items: center; flex-wrap: wrap; }
        .mis-page .time-select { width: 160px; }
        .mis-detail-title { font-size: var(--text-lg); font-weight: 600; margin: var(--space-8) 0 var(--space-4); padding-top: var(--space-6); border-top: 1px solid var(--divider); }

        .pa-card {
          background: linear-gradient(180deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.05) 100%);
          border: 1px solid rgba(255,255,255,0.14);
          border-radius: 16px;
          padding: var(--space-6);
          margin-bottom: var(--space-6);
        }
        .pa-error { display: flex; align-items: center; gap: var(--space-2); padding: var(--space-3) var(--space-4); margin-bottom: var(--space-6); background: var(--status-danger-subtle); color: var(--status-danger); border-radius: var(--radius-md); font-size: var(--text-sm); }
        .pa-loading { display: flex; justify-content: center; padding: var(--space-10) 0; color: var(--text-tertiary); }
        .pa-empty { display: flex; align-items: center; gap: var(--space-3); padding: var(--space-6); color: var(--text-tertiary); font-size: var(--text-sm); }

        .pa-metrics { display: grid; grid-template-columns: repeat(4, 1fr); gap: var(--space-5); margin-bottom: var(--space-6); }
        .pa-metrics .pa-card { margin-bottom: 0; }
        .pa-metric__label { display: block; font-size: var(--text-sm); color: var(--text-secondary, var(--text-primary)); margin-bottom: var(--space-2); }
        .pa-metric__value { display: block; font-size: var(--text-3xl); font-weight: 600; letter-spacing: -0.02em; }
        .pa-metric__sub { display: block; font-size: var(--text-xs); color: var(--text-tertiary); margin-top: var(--space-1); }

        .pa-title { font-size: var(--text-lg); font-weight: 600; margin-bottom: var(--space-4); }
        .pa-note { font-size: var(--text-xs); color: var(--text-tertiary); margin-top: var(--space-3); }
        .pa-table-wrap { overflow-x: auto; }
        .pa-table { width: 100%; border-collapse: collapse; font-size: var(--text-sm); }
        .pa-table th { text-align: left; font-weight: 500; color: var(--text-tertiary); padding: var(--space-2) var(--space-3); border-bottom: 1px solid rgba(255,255,255,0.1); }
        .pa-table td { padding: var(--space-3); border-bottom: 1px solid rgba(255,255,255,0.06); }
        .pa-table td:first-child { color: var(--text-primary); }
        .pa-table th:not(:first-child), .pa-table td:not(:first-child) { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
        .pa-defs { margin: 0; padding-left: var(--space-5); font-size: var(--text-sm); color: var(--text-tertiary); line-height: 1.6; }
        .pa-defs li { margin-bottom: var(--space-2); }
        .pa-defs strong { color: var(--text-primary); font-weight: 600; }

        @media (max-width: 900px) { .pa-metrics { grid-template-columns: repeat(2, 1fr); } }
      `}</style>
    </div>
  );
}
